import type * as React from "react";

import classNames from "classnames";
import { useUser } from "hooks/useUser";
import { type UserModel } from "models/UserModel";
import { useMemo } from "react";

import { useStyles } from "./styles";

interface IAvatarProps {
  fullName?: string;
  imageUrl?: string;
  initials?: string;
  size?: "large" | "medium" | "small";
  user?: UserModel;
  userId?: string;
}

export const Avatar: FC<IAvatarProps> = (props) => {
  const { className, ref, size = "small", user: userProp, userId, ...userProps } = props;
  const fetchedUser = useUser(userId);
  const user = useMemo(() => userProp || (userId ? fetchedUser : null), [userProp, userId, fetchedUser]);
  const classes = useStyles();
  const text = useMemo(() => getText(user ? { fullName: user.fullName } : userProps), [user, userProps]);
  const style = useMemo(
    () => getStyle(user && user.imageUrl ? { imageUrl: user.imageUrl } : userProps),
    [user, userProps],
  );
  return (
    <div
      className={classNames(classes.avatar, size, className)}
      ref={ref as React.Ref<HTMLDivElement>}
      style={style}
    >
      {!style?.backgroundImage && text}
    </div>
  );
};

const getStyle = (props: Partial<IAvatarProps>): { backgroundImage?: string } => {
  const { imageUrl } = props;
  if (imageUrl) {
    return {
      backgroundImage: `url(${imageUrl})`,
    };
  }
  return {};
};

const getText = (props: Partial<IAvatarProps>): string => {
  const { fullName, initials } = props;
  if (fullName) {
    return getInitials(fullName);
  }
  if (initials) {
    return initials;
  }
  return "";
};

const getInitials = (fullName: string): string => {
  const parts = fullName.split(" ", 2);
  return parts.map((part) => part?.[0]?.toLocaleUpperCase()).join("");
};
